import React, { useState } from "react";
import { MapPin, Phone, Mail, Send, Clock } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import PageHero from "../components/PageHero";
import { SITE_ASSETS } from "../mock";
import { toast } from "sonner";

const initial = { name: "", email: "", phone: "", project: "", message: "" };

export default function ContactPage() {
  const { settings, addSubmission } = useSettings();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await addSubmission(form);
      toast.success("Thank you. Our team will reach out within 24 hours.");
      setForm(initial);
    } catch (err) {
      toast.error("Could not send message. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <>
      <PageHero
        eyebrow="Reach Tamra"
        title="Begin the conversation."
        italicWord="conversation"
        description="Whether it's a private viewing, an investor enquiry or a partnership — we read every message and respond personally within 24 hours."
        image={SITE_ASSETS.hero.playground}
      />

      <section className="pb-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          {/* Form */}
          <form onSubmit={onSubmit} className="lg:col-span-7 bg-white p-8 lg:p-12 border border-[#EBE0CE] space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Your Name" value={form.name} onChange={onChange("name")} required />
              <Field label="Email" type="email" value={form.email} onChange={onChange("email")} required />
              <Field label="Phone" value={form.phone} onChange={onChange("phone")} />
              <Field label="Interested Project" value={form.project} onChange={onChange("project")} placeholder="Tamra Asteria, Vista, etc." />
            </div>
            <Field label="Message" textarea value={form.message} onChange={onChange("message")} required />
            <button disabled={loading} type="submit" className="btn-primary disabled:opacity-60">
              <Send size={14} /> {loading ? "Sending..." : "Send Message"}
            </button>
          </form>

          {/* Contact details */}
          <div className="lg:col-span-5 tamra-contact-stack">
            <Detail icon={<MapPin />} title="Office" value={settings.contact.address} />
            <Detail icon={<Phone />}  title="Phone"  value={settings.contact.phone} href={`tel:${settings.contact.phone}`} />
            <Detail icon={<Mail />}   title="Email"  value={settings.contact.email} href={`mailto:${settings.contact.email}`} />
            <Detail icon={<Clock />}  title="Hours"  value={settings.contact.hours} />
            <div className="aspect-[4/3] overflow-hidden border border-[#EBE0CE]">
              <iframe
                title="Map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=78.30%2C17.32%2C78.60%2C17.55&layer=mapnik"
                className="w-full h-full"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, textarea, ...props }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.4em] uppercase text-[#B8893C]">{label}</span>
      {textarea ? (
        <textarea
          rows={5}
          {...props}
          className="mt-2 w-full bg-transparent border-b border-[#EBE0CE] focus:border-[#B8893C] outline-none py-3 text-[#1F1B17] resize-none"
        />
      ) : (
        <input
          {...props}
          className="mt-2 w-full bg-transparent border-b border-[#EBE0CE] focus:border-[#B8893C] outline-none py-3 text-[#1F1B17]"
        />
      )}
    </label>
  );
}

function Detail({ icon, title, value, href }) {
  const inner = (
    <div className="tamra-contact-item">
      <div className="tamra-contact-icon">
        {icon}
      </div>
      <div className="tamra-contact-text">
        <div className="tamra-contact-title">{title}</div>
        <div className="tamra-contact-value">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block">{inner}</a> : inner;
}
